import { useEffect, useState } from 'react';
import './Countdown.css';

interface CountdownProps {
  /** Starting number. Counts down to 1, then completes. */
  from: number;
  onTick?: (value: number) => void;
  onComplete: () => void;
}

/**
 * The three seconds before release.
 *
 * Deliberately the loudest moment in the product: everything else is understated
 * so this can carry the tension. One interval, one piece of state, and a `key`
 * on the numeral so the entry animation replays for each count.
 */
export function Countdown({ from, onTick, onComplete }: CountdownProps) {
  // Elapsed whole seconds since mount. The displayed numeral is derived from
  // it, so there is no state to keep in step with the interval and no reset to
  // perform — the parent starts a new run by changing our `key`.
  const [elapsed, setElapsed] = useState(0);
  const value = Math.max(1, from - elapsed);
  const finished = elapsed >= from;

  useEffect(() => {
    if (finished) return;
    const id = setInterval(() => setElapsed((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [finished]);

  // Notifications fire from the render they belong to, keyed on `value`, so
  // each numeral pips exactly once even under StrictMode's double-invoke.
  useEffect(() => {
    if (!finished) onTick?.(value);
  }, [value, finished, onTick]);

  useEffect(() => {
    if (finished) onComplete();
  }, [finished, onComplete]);

  return (
    <div className="countdown" role="timer" aria-live="assertive" aria-atomic="true">
      <div className="countdown__scrim" />
      <span className="countdown__ring" aria-hidden="true" />
      <span key={value} className="countdown__value">
        {value}
      </span>
      <p className="countdown__label">
        <span className="visually-hidden">Releasing Jeff in </span>
        {value} — RELEASING JEFF
      </p>
    </div>
  );
}
